import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, CircleAlert, ClipboardList, HelpCircle, ListTodo, Mail, MessageSquareText, Save, Search, UserCheck, UserRound } from 'lucide-react';
import { api } from '../lib/api';
import { Breadcrumbs, Card, PageHeader, Status } from '../components/UI';

const post=body=>({method:'POST',headers:{'content-type':'application/json'},body:JSON.stringify(body)});

const ISSUE_TYPES=[
  {value:'request',label:'Request',icon:ClipboardList,description:'The customer wants something done, changed or provided.'},
  {value:'question',label:'Question / enquiry',icon:HelpCircle,description:'The customer needs an answer or information.'},
  {value:'complaint',label:'Complaint',icon:AlertTriangle,description:'The customer is dissatisfied and needs the complaint workflow.'},
  {value:'task',label:'Task / follow-up',icon:ListTodo,description:'A piece of customer-related work that needs to be completed.'},
  {value:'incident',label:'Service issue',icon:CircleAlert,description:'A fault, failure or service problem affecting the customer.'},
  {value:'feedback',label:'Feedback',icon:MessageSquareText,description:'A compliment, suggestion, concern or general feedback.'},
];

const REQUEST_TYPES={
  request:[
    ['general','General request'],
    ['account_support','Account support / change'],
    ['billing_query','Billing or payment request'],
    ['technical_support','Technical support'],
    ['service_request','Service request'],
    ['order_support','Order / delivery request'],
    ['sales_admin','Sales / administration'],
    ['other','Other request'],
  ],
  question:[
    ['general_question','General question'],
    ['account_question','Account question'],
    ['billing_question','Billing / payment question'],
    ['technical_question','Technical question'],
    ['product_service_question','Product / service question'],
    ['order_question','Order / delivery question'],
    ['sales_question','Sales / pricing question'],
    ['policy_terms_question','Policy / terms question'],
    ['other_question','Other question'],
  ],
  task:[
    ['follow_up','Follow-up'],
    ['callback','Callback'],
    ['account_review','Account review'],
    ['billing_follow_up','Billing follow-up'],
    ['fulfilment_follow_up','Order / fulfilment follow-up'],
    ['internal_action','Internal action for customer'],
    ['other_task','Other task'],
  ],
  incident:[
    ['service_issue','Service issue'],
    ['access_issue','Access / sign-in issue'],
    ['payment_issue','Payment issue'],
    ['order_delivery_issue','Order / delivery issue'],
    ['outage_degradation','Outage / service degradation'],
    ['other_incident','Other service issue'],
  ],
  feedback:[
    ['compliment','Compliment'],
    ['suggestion','Suggestion'],
    ['service_feedback','Service feedback'],
    ['concern','Concern'],
    ['other_feedback','Other feedback'],
  ],
};

const DEFAULT_REQUEST_TYPE={
  request:'general',
  question:'general_question',
  task:'follow_up',
  incident:'service_issue',
  feedback:'service_feedback',
};

const ISSUE_LABEL={
  request:'Request',
  question:'Question',
  complaint:'Complaint',
  task:'Task',
  incident:'Service issue',
  feedback:'Feedback',
};

const RELATIONSHIPS=[
  ['customer','Customer themself'],
  ['authorised_representative','Authorised representative'],
  ['parent_guardian','Parent / guardian'],
  ['appointee','Appointee'],
  ['attorney_deputy','Attorney / deputy'],
  ['other','Other'],
];

const emptyForm=(issueType='request')=>({
  issue_type:issueType,
  request_type:DEFAULT_REQUEST_TYPE[issueType]||'general',
  channel:'phone',
  acting_capacity:'customer',
  reporter_name:'',
  reporter_email:'',
  title:'',
  description:'',
  priority:'normal',
  due_at:'',
  assigned_staff_id:'',
  send_acknowledgement:true,
});

export default function RequestIntake(){
  const {id}=useParams();
  const navigate=useNavigate();
  const[searchParams]=useSearchParams();
  const initialIssueType=ISSUE_TYPES.some(item=>item.value===searchParams.get('type'))?searchParams.get('type'):'request';
  const[customer,setCustomer]=useState(null);
  const[query,setQuery]=useState('');
  const[results,setResults]=useState([]);
  const[searching,setSearching]=useState(false);
  const[loadingCustomer,setLoadingCustomer]=useState(Boolean(id));
  const[form,setForm]=useState(()=>emptyForm(initialIssueType));
  const[busy,setBusy]=useState(false);
  const[error,setError]=useState('');
  const[result,setResult]=useState(null);

  const set=(name,value)=>setForm(current=>({...current,[name]:value}));
  const isCustomer=form.acting_capacity==='customer';
  const issueLabel=ISSUE_LABEL[form.issue_type]||'Request';
  const issueTypes=REQUEST_TYPES[form.issue_type]||[];

  const chooseIssueType=value=>{
    setError('');
    setForm(current=>({...current,issue_type:value,request_type:DEFAULT_REQUEST_TYPE[value]||''}));
  };

  const openComplaint=()=>{
    if(!customer)return;
    navigate('/customers/'+encodeURIComponent(customer.id)+'/complaints/new');
  };

  const selectCustomer=row=>{
    setCustomer(row);
    setResults([]);
    setQuery('');
    setForm(current=>({
      ...current,
      reporter_name:current.acting_capacity==='customer'?(row.display_name||''):current.reporter_name,
      reporter_email:current.acting_capacity==='customer'?(row.verified_email||''):current.reporter_email,
    }));
  };

  useEffect(()=>{
    if(!id)return;
    let active=true;
    setLoadingCustomer(true);
    api('/api/customers/'+encodeURIComponent(id)).then(body=>{
      if(!active)return;
      const row=body?.customer||body;
      if(!row?.id)throw new Error('The customer could not be loaded.');
      selectCustomer(row);
    }).catch(e=>active&&setError(e.message)).finally(()=>active&&setLoadingCustomer(false));
    return()=>{active=false};
  },[id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(()=>{
    if(customer)return undefined;
    const value=query.trim();
    if(value.length<2){setResults([]);return undefined}
    const timer=setTimeout(async()=>{
      setSearching(true);setError('');
      try{
        const body=await api('/api/cases/customer-candidates?q='+encodeURIComponent(value));
        setResults(body.customers||[]);
      }catch(e){setResults([]);setError(e.message)}
      finally{setSearching(false)}
    },220);
    return()=>clearTimeout(timer);
  },[query,customer]);

  useEffect(()=>{
    if(!customer||!isCustomer)return;
    setForm(current=>({...current,reporter_name:customer.display_name||'',reporter_email:customer.verified_email||''}));
  },[customer,isCustomer]);

  const canSubmit=useMemo(()=>Boolean(customer&&form.issue_type!=='complaint'&&form.request_type&&form.title.trim()&&form.description.trim()&&form.reporter_name.trim()),[customer,form.issue_type,form.request_type,form.title,form.description,form.reporter_name]);

  const submit=async event=>{
    event.preventDefault();
    if(form.issue_type==='complaint'){openComplaint();return}
    if(!canSubmit){setError('Select the customer and complete the required work-item details.');return}
    setBusy(true);setError('');setResult(null);
    try{
      const body=await api('/api/cases',post({
        customer_id:customer.id,
        ...form,
        due_at:form.due_at?new Date(form.due_at).toISOString():null,
        created_on_behalf:true,
      }));
      setResult(body);
    }catch(e){setError(e.message)}
    finally{setBusy(false)}
  };

  if(result){
    const created=result.case||{};
    return <div className="request-intake-page request-intake-page-complete">
      <Breadcrumbs items={['Cases','Create work item','Created']} />
      <PageHeader eyebrow="Customer Operations" title={issueLabel+' created'} description={'The '+issueLabel.toLowerCase()+' has been recorded on the customer record with a permanent staff audit trail.'} />
      <div className="request-intake-confirmation">
        <CheckCircle2 size={42}/>
        <div><h2 className="govuk-heading-m">{created.case_reference||created.reference}</h2><p className="govuk-body">{issueLabel} created on behalf of {customer?.display_name||'the customer'}.</p></div>
      </div>
      <div className="request-intake-summary">
        <Card title="Work item">
          <dl className="request-intake-readback">
            <div><dt>Reference</dt><dd>{created.case_reference||created.reference||'—'}</dd></div>
            <div><dt>Type</dt><dd>{issueLabel}</dd></div>
            <div><dt>Category</dt><dd>{String(form.request_type||'—').replaceAll('_',' ')}</dd></div>
            <div><dt>Customer</dt><dd>{customer?.display_name||'—'}</dd></div>
            <div><dt>Created by</dt><dd>{result.created_by?.name||'Customer Operations'}</dd></div>
            <div><dt>Channel</dt><dd>{String(form.channel).replaceAll('_',' ')}</dd></div>
          </dl>
        </Card>
        <Card title="Customer acknowledgement">
          <div className={'request-intake-ack '+(result.acknowledgement?.sent?'sent':result.acknowledgement?.skipped?'skipped':'failed')}>
            <Mail size={21}/>
            <div>
              <strong>{result.acknowledgement?.sent?'Acknowledgement email sent':result.acknowledgement?.skipped?'Acknowledgement not requested':'Acknowledgement needs attention'}</strong>
              <span>{result.acknowledgement?.recipient||form.reporter_email||customer?.verified_email||'No email route'}</span>
              {result.warning&&<small>{result.warning}</small>}
            </div>
          </div>
        </Card>
      </div>
      <div className="request-intake-complete-actions">
        <button className="button primary govuk-button" onClick={()=>navigate('/cases/'+encodeURIComponent(created.id))}>Open work item</button>
        <button className="button secondary govuk-button govuk-button--secondary" onClick={()=>navigate('/customers/'+encodeURIComponent(customer.id))}>Return to Customer360</button>
        <Link className="button secondary govuk-button govuk-button--secondary" to={'/customers/'+encodeURIComponent(customer.id)+'/requests/new'}>Create another</Link>
      </div>
    </div>;
  }

  return <div className="request-intake-page">
    <Breadcrumbs items={['Cases','Create work item']} />
    <PageHeader
      eyebrow="Customer Operations"
      title="Create work item on behalf of a customer"
      description="Choose the work item type first, then record the customer details, category, priority and assignment."
      actions={<Link className="button secondary govuk-button govuk-button--secondary" to={customer?'/customers/'+encodeURIComponent(customer.id):'/cases'}><ArrowLeft size={15}/>Back</Link>}
    />

    {error&&<div className="request-intake-error">{error}</div>}

    {!customer&&<Card title="1. Find the customer" className="request-intake-card request-intake-search-card">
      <div className="request-intake-search">
        <label className="govuk-label"><span>Name, customer number, email or phone</span><div><Search size={17}/><input className="govuk-input" value={query} onChange={e=>setQuery(e.target.value)} placeholder="Start typing to search Customer360" autoFocus/></div></label>
        {searching&&<span className="request-intake-search-state">Searching Customer360…</span>}
        {query.trim().length>=2&&!searching&&<div className="request-intake-results">
          {results.length?results.map(row=><button type="button" key={row.id} onClick={()=>selectCustomer(row)}>
            <UserCheck size={16}/><span><strong>{row.display_name}</strong><small>{row.customer_number||row.id} · {row.verified_email||'No verified email'}{row.telephone?' · '+row.telephone:''}</small></span>
          </button>):<span>No matching customers.</span>}
        </div>}
      </div>
    </Card>}

    {loadingCustomer&&!customer&&<div className="request-intake-loading">Loading customer…</div>}

    {customer&&<form className="request-intake-form" onSubmit={submit}>
      <Card title="1. Customer" className="request-intake-card request-intake-customer-card">
        <div className="request-intake-customer">
          <div className="request-intake-avatar">{String(customer.display_name||'?').split(/\s+/).slice(0,2).map(value=>value[0]).join('').toUpperCase()}</div>
          <div><strong>{customer.display_name}</strong><span>Customer #{customer.customer_number||'—'} · <Status value={customer.account_status||'unknown'}/></span><small>{customer.verified_email||'No verified email recorded'}</small></div>
          {!id&&<button type="button" className="text-link" onClick={()=>{setCustomer(null);setQuery('');setResults([])}}>Change customer</button>}
        </div>
        <div className="request-intake-on-behalf"><UserRound size={18}/><div><strong>Staff-assisted request</strong><span>This record will state that Customer Operations created it on the customer's behalf. It will not be represented as customer self-service.</span></div></div>
      </Card>

      <Card title="2. Work item type" className="request-intake-card request-intake-details-card">
        <div className="request-issue-type-grid">
          {ISSUE_TYPES.map(item=>{
            const Icon=item.icon;
            return <button type="button" key={item.value} className={'request-issue-type '+(form.issue_type===item.value?'active':'')} onClick={()=>chooseIssueType(item.value)}>
              <Icon size={18}/><span><strong>{item.label}</strong><small>{item.description}</small></span>{form.issue_type===item.value&&<CheckCircle2 size={16}/>}
            </button>;
          })}
        </div>
        {form.issue_type==='complaint'?<div className="request-complaint-route">
          <AlertTriangle size={22}/>
          <div><strong>Complaints use the formal complaint workflow</strong><span>The complaint intake includes complaint level, service area, complaint officer, acknowledgement, investigation and outcome controls.</span></div>
          <button type="button" className="button primary govuk-button" onClick={openComplaint}>Open complaint intake</button>
        </div>:<div className="request-intake-grid">
          <Field label={issueLabel+' category'} required><select className="govuk-select" value={form.request_type} onChange={e=>set('request_type',e.target.value)}>{issueTypes.map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></Field>
          <Field label="How was it received?" required><select className="govuk-select" value={form.channel} onChange={e=>set('channel',e.target.value)}><option value="phone">Phone</option><option value="email">Email</option><option value="whatsapp">WhatsApp</option><option value="letter">Letter</option><option value="in_person">In person</option><option value="staff_initiated">Staff initiated</option><option value="other">Other</option></select></Field>
          <Field label="Priority"><select className="govuk-select" value={form.priority} onChange={e=>set('priority',e.target.value)}><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="critical">Critical</option></select></Field>
          <Field label="Target / due date"><input className="govuk-input" type="datetime-local" value={form.due_at} onChange={e=>set('due_at',e.target.value)}/></Field>
          <Field label={issueLabel+' title'} required wide><input className="govuk-input" value={form.title} onChange={e=>set('title',e.target.value)} placeholder={'Short, clear summary of the '+issueLabel.toLowerCase()}/></Field>
          <Field label={issueLabel+' details'} required wide><textarea className="govuk-textarea" rows="7" value={form.description} onChange={e=>set('description',e.target.value)} placeholder="Record the customer's words, relevant dates, products or services, references and any action already taken."/></Field>
        </div>}
      </Card>

      {form.issue_type!=='complaint'&&<Card title="3. Customer authority and contact" className="request-intake-card request-intake-authority-card">
        <div className="request-intake-grid">
          <Field label="Who is making this contact?" required><select className="govuk-select" value={form.acting_capacity} onChange={e=>set('acting_capacity',e.target.value)}>{RELATIONSHIPS.map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></Field>
          <Field label="Requester / contact name" required><input className="govuk-input" value={form.reporter_name} onChange={e=>set('reporter_name',e.target.value)} readOnly={isCustomer}/></Field>
          <Field label="Requester / contact email"><input className="govuk-input" type="email" value={form.reporter_email} onChange={e=>set('reporter_email',e.target.value)} readOnly={isCustomer}/></Field>
          <label className="request-intake-checkbox govuk-label"><input type="checkbox" checked={form.assigned_staff_id==='me'} onChange={e=>set('assigned_staff_id',e.target.checked?'me':'')}/><span><strong>Assign this work item to me</strong><small>Leave unticked to put an unassigned staff-created work item into Triage.</small></span></label>
          <label className="request-intake-checkbox wide govuk-label"><input type="checkbox" checked={form.send_acknowledgement} onChange={e=>set('send_acknowledgement',e.target.checked)}/><span><strong>Email an acknowledgement now</strong><small>The customer or recorded requester will receive the work-item reference using the governed Customer Operations template.</small></span></label>
        </div>
      </Card>}

      {form.issue_type!=='complaint'&&<div className="request-intake-submit">
        <div><small>CUSTOMER OPERATIONS WORK ITEM · {issueLabel.toUpperCase()}</small><strong>Create {issueLabel.toLowerCase()} on behalf of {customer.display_name}</strong><span>The audit trail will record the issue type, category, staff member, customer, source channel, authority and creation time.</span></div>
        <button className="button primary govuk-button" type="submit" disabled={busy||!canSubmit}><Save size={16}/>{busy?'Creating…':'Create '+issueLabel.toLowerCase()}</button>
      </div>}
    </form>}
  </div>;
}

function Field({label,required,wide,children}){
  return <label className={'request-intake-field'+(wide?' wide':'')}><span>{label}{required&&<b> *</b>}</span>{children}</label>;
}
